export interface PippaSession {
  lastActivityAt: number;
  chatId: string;
}

const PIPPA_SESSION_STORAGE_KEY = "pippa_session";
const RATINGS_STORAGE_KEY = "pippa_ratings";
const PASSED_OR_SKIPPED_COMPONENT_ID_STORAGE_KEY =
  "pippa_passed_or_skipped_component_id";

const getFromStorage = <T>(key: string): T | null => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const setToStorage = <T>(key: string, value: T): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const removeFromStorage = (key: string): void => {
  localStorage.removeItem(key);
};

export const getSession = (): PippaSession | null => {
  const session = getFromStorage<PippaSession>(PIPPA_SESSION_STORAGE_KEY);
  return session?.chatId ? session : null;
};

export function isActiveSession(
  session: PippaSession | null,
  ttlMs: number
): boolean {
  if (!session) {
    return false;
  }
  return Date.now() - session.lastActivityAt < ttlMs;
}

export function updateSessionActivity(
  chatId: string,
  lastActivityAt?: number
): void {
  const session: PippaSession = {
    lastActivityAt: lastActivityAt ?? Date.now(),
    chatId,
  };
  setToStorage(PIPPA_SESSION_STORAGE_KEY, session);
}

const getRatingsMap = (): Record<string, number> =>
  getFromStorage<Record<string, number>>(RATINGS_STORAGE_KEY) ?? {};

export const getRatingScore = (messageId: string): number => {
  if (!messageId) return -1;
  const value = getRatingsMap()[messageId];
  if (value != null) {
    return Number(value);
  }
  return -1;
};

export const setRatingScore = (messageId: string, score: number): void => {
  if (!messageId || score < 0) return;
  const ratings = getRatingsMap();
  setToStorage(RATINGS_STORAGE_KEY, {
    ...ratings,
    [messageId]: score,
  });
};

export const getPassedOrSkippedFlowComponentsIdFromStorage = (): string[] =>
  getFromStorage<string[]>(PASSED_OR_SKIPPED_COMPONENT_ID_STORAGE_KEY) ?? [];

export const addPassedOrSkippedFlowComponentIdToStorage = (
  id: string
): void => {
  const list = getPassedOrSkippedFlowComponentsIdFromStorage();
  if (!list.includes(id)) {
    setToStorage(PASSED_OR_SKIPPED_COMPONENT_ID_STORAGE_KEY, [...list, id]);
  }
};

export const clearStorage = (): void => {
  removeFromStorage(PIPPA_SESSION_STORAGE_KEY);
  removeFromStorage(RATINGS_STORAGE_KEY);
  removeFromStorage(PASSED_OR_SKIPPED_COMPONENT_ID_STORAGE_KEY);
};
